import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Send, Flag, Ban } from "lucide-react";
import { toast } from "sonner";
import ReportDialog from "@/components/ReportDialog";

function conversationId(a, b, productId) {
  return `${[a, b].sort().join("_")}__${productId || "general"}`;
}

export default function Messages() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const toId = params.get("to");
  const productId = params.get("product");

  const [conversations, setConversations] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [otherUser, setOtherUser] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [blockedIds, setBlockedIds] = useState([]);
  const scrollRef = useRef(null);

  useEffect(() => { if (!loading && !user) navigate("/login"); }, [user, loading, navigate]);

  const loadConvs = async () => {
    const { data } = await api.get("/messages/conversations");
    setConversations(data);
  };

  useEffect(() => { if (user) { loadConvs(); api.get("/blocks").then(r => setBlockedIds(r.data)).catch(()=>{}); } }, [user]);

  const otherBlocked = otherUser && blockedIds.includes(otherUser.user_id);

  const toggleBlock = async () => {
    if (!otherUser) return;
    try {
      if (otherBlocked) {
        await api.delete(`/blocks/${otherUser.user_id}`);
        setBlockedIds(blockedIds.filter(i => i !== otherUser.user_id));
        toast.success("Utilisateur débloqué");
      } else {
        await api.post(`/blocks/${otherUser.user_id}`);
        setBlockedIds([...blockedIds, otherUser.user_id]);
        toast.success("Utilisateur bloqué");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  useEffect(() => {
    if (toId && user) {
      const cid = conversationId(user.user_id, toId, productId);
      setActive(cid);
      api.get(`/users/${toId}`).then(r => setOtherUser(r.data)).catch(()=>{});
    }
  }, [toId, productId, user]);

  useEffect(() => {
    if (!active) return;
    api.get(`/messages/${active}`).then(r => setMessages(r.data)).catch(() => setMessages([]));
  }, [active]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !otherUser) return;
    try {
      const { data } = await api.post("/messages", {
        receiver_id: otherUser.user_id,
        product_id: productId,
        content: text.trim(),
      });
      setMessages([...messages, data]);
      setText("");
      loadConvs();
    } catch (e) { toast.error(e.response?.data?.detail || "Message non envoyé"); }
  };

  const openConv = async (c) => {
    setActive(c.conversation_id);
    setOtherUser(c.other_user);
  };

  if (!user) return <div className="p-20 text-center">Chargement…</div>;

  return (
    <div className="max-w-[1400px] mx-auto px-4 lg:px-8 py-6 lg:py-10 fade-in has-bottom-nav" data-testid="messages-page">
      <p className="label mb-2">Messages</p>
      <h1 className="text-3xl lg:text-5xl font-black tracking-tighter mb-8">Conversations</h1>

      <div className="grid grid-cols-1 lg:grid-cols-12 border-2 border-black bg-white" style={{minHeight: 600}}>
        <div className="lg:col-span-4 lg:border-r-2 border-black">
          <div className="p-3 hairline-b">
            <p className="label font-bold">{conversations.length} discussion(s)</p>
          </div>
          <div className="max-h-[600px] overflow-y-auto">
            {conversations.length === 0 && <p className="p-6 text-sm text-center text-[color:var(--color-text-muted)]">Aucune conversation.</p>}
            {conversations.map(c => (
              <button key={c.conversation_id}
                onClick={()=>openConv(c)}
                className={`w-full text-left p-3 hairline-b flex gap-3 ${active===c.conversation_id ? 'bg-[color:var(--color-mint)]' : 'hover:bg-[color:var(--color-surface)]'}`}
                data-testid={`conv-${c.conversation_id}`}
              >
                <div className="w-10 h-10 shrink-0 bg-black text-white flex items-center justify-center font-bold">
                  {c.other_user?.picture ? <img src={c.other_user.picture} className="w-full h-full object-cover"/> : (c.other_user?.name?.[0] || "?")}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold truncate">{c.other_user?.name}</p>
                  <p className="text-xs truncate text-[color:var(--color-text-muted)]">{c.last_message}</p>
                  {c.product && <p className="label mt-0.5 truncate">{c.product.title}</p>}
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="lg:col-span-8 flex flex-col" style={{minHeight: 600}}>
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-[color:var(--color-text-muted)]">
              Sélectionne une conversation
            </div>
          ) : (
            <>
              <div className="p-3 hairline-b flex items-center gap-3">
                {otherUser && (
                  <>
                    <div className="w-10 h-10 bg-black text-white flex items-center justify-center font-bold">
                      {otherUser.picture ? <img src={otherUser.picture} className="w-full h-full object-cover"/> : (otherUser.name?.[0] || "?")}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold leading-tight truncate">{otherUser.name}</p>
                      <p className="label">Vendeur</p>
                    </div>
                    <button onClick={()=>setShowReport(true)} className="w-9 h-9 border-2 border-black flex items-center justify-center hover:bg-[color:var(--color-coral)] hover:text-white shrink-0" title="Signaler" aria-label="Signaler cet utilisateur" data-testid="report-conv-btn"><Flag size={14}/></button>
                    <button onClick={toggleBlock} className={`w-9 h-9 border-2 border-black flex items-center justify-center shrink-0 ${otherBlocked ? "bg-[color:var(--color-coral)] text-white" : "hover:bg-[color:var(--color-yellow)]"}`} title={otherBlocked ? "Débloquer" : "Bloquer"} aria-label={otherBlocked ? "Débloquer cet utilisateur" : "Bloquer cet utilisateur"} data-testid="block-conv-btn"><Ban size={14}/></button>
                  </>
                )}
              </div>
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2">
                {messages.length === 0 && <p className="text-sm text-center text-[color:var(--color-text-muted)]">Démarre la conversation.</p>}
                {messages.map(m => (
                  <div key={m.message_id} className={`flex ${m.sender_id===user.user_id ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[70%] p-3 text-sm border-2 border-black ${m.sender_id===user.user_id ? 'bg-[color:var(--color-mint)]' : 'bg-white'}`}>
                      {m.content}
                      <p className="text-[10px] mt-1 opacity-60">{new Date(m.created_at).toLocaleTimeString("fr-FR",{hour:"2-digit",minute:"2-digit"})}</p>
                    </div>
                  </div>
                ))}
              </div>
              <form onSubmit={send} className="p-3 hairline-t flex gap-2">
                {otherBlocked ? (
                  <p className="text-xs text-center w-full py-2 text-[color:var(--color-text-muted)] font-bold">Utilisateur bloqué — débloquez-le pour reprendre la conversation.</p>
                ) : (
                  <>
                    <input className="field flex-1" placeholder="Ton message…" value={text} onChange={e=>setText(e.target.value)} data-testid="message-input" />
                    <button className="btn btn-primary px-4" type="submit" data-testid="message-send"><Send size={16} strokeWidth={2.5}/></button>
                  </>
                )}
              </form>
            </>
          )}
        </div>
      </div>
      {showReport && otherUser && (
        <ReportDialog targetType="user" targetId={otherUser.user_id} targetLabel={otherUser.name} onClose={()=>setShowReport(false)}/>
      )}
    </div>
  );
}
